import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lock, Ban } from "lucide-react";
import { PageHead, Wrap, H2, Lead, P, Prose, Impact, Callout } from "@/components/Content";
import { CinematicSection } from "@/components/CinematicSection";
import { ASSETS } from "@/lib/api";

export default function AboutLab() {
  return (
    <div className="bg-ink min-h-screen pb-24">
      <CinematicSection
        image={ASSETS.madMax}
        contain
        alt="The Scientist working inside the CardScience HQ laboratory"
        label="About The Lab"
        headline="INSIDE THE LAB."
        accent="MEET THE SCIENTIST."
        sentence="Decades of collecting knowledge, one modern collectibles laboratory. Built by collectors, for collectors who want their collection to be the best version of itself."
        primaryTo="/submit"
        primaryLabel="SUBMIT A COLLECTIBLE"
        secondaryTo="/trust-center"
        secondaryLabel="Our Standards"
      />

      <Wrap className="py-16">
        <Prose>
          <Lead>
            CardScience HQ was built on more than 60 years of collectible experience spanning comic books,
            stamps, vintage baseball cards, modern sports cards, Pokémon, trading cards and other collectible
            paper-based items.
          </Lead>
          <P>Our history did not begin with grading trends, social media or the latest collectible boom.</P>
          <Impact>It began with collectors.</Impact>
          <P>
            It began with understanding condition, preservation, presentation, storage, handling and the
            responsibility that comes with being trusted with something another collector values.
          </P>
          <P>Kit Allen is the owner behind iHeartCards and iHeartPokemon.</P>
          <P>Our modern card operation began in 2020.</P>
          <P>
            In 2026, that experience evolved into CardScience HQ — a dedicated collectibles laboratory focused
            on inspection, cleaning, preparation, preservation and presentation.
          </P>
          <P>Today, CardScience HQ serves collectors across all 50 states.</P>
          <P>
            Our location in the Dallas market has placed extraordinary collections and significant cards in
            front of our team. Some of those collectibles carry substantial financial value. Others carry
            something far more important to their owners: history, memories and sentimental value.
          </P>
          <Impact tone="holo">To us, both deserve respect.</Impact>
          <div className="pt-2">
            <Impact>Every collectible has a story.</Impact>
            <Impact>Every collector deserves discretion.</Impact>
          </div>
        </Prose>
      </Wrap>

      {/* Turned away */}
      <Wrap>
        <Callout holo>
          <p className="eyebrow mb-3 flex items-center gap-2"><Ban size={13} className="text-[color:var(--magenta)]" /> We Turn Cards Away</p>
          <H2>MORE THAN 30% OF THE CARDS WE EVALUATE ARE TURNED AWAY.</H2>
          <p className="text-[color:var(--chrome-2)] mt-3 italic">Sometimes the smartest thing we can do is nothing.</p>
          <Prose className="mt-6">
            <P>Not every card should be cleaned.</P>
            <P>Not every comic should be treated.</P>
            <P>Not every collectible is a good candidate for our process.</P>
            <P>
              More than 30% of the cards we evaluate are turned away because we believe the risk outweighs the
              potential benefit, the damage cannot responsibly be addressed, the collectible is not appropriate
              for our process, or the proposed service would provide little meaningful benefit.
            </P>
            <P>We would rather tell a collector not to spend their money than perform work simply because someone is willing to pay us.</P>
            <P className="text-white font-semibold">Turning away a collectible is part of our process. It is not a failure of our process.</P>
            <P>
              The remaining approximately 70% are collectibles that, after evaluation, we believe may be
              appropriate candidates for the services we offer.
            </P>
            <P>Acceptance does not guarantee improvement. Acceptance does not guarantee grading eligibility. Acceptance does not guarantee a particular result.</P>
            <P>It means only that, based on our evaluation, we are willing to perform the approved service.</P>
          </Prose>
        </Callout>
      </Wrap>

      {/* Confidentiality teaser */}
      <Wrap className="mt-12">
        <Callout>
          <p className="eyebrow mb-3 flex items-center gap-2"><Lock size={13} className="text-[color:var(--cyan)]" /> Confidentiality</p>
          <H2>YOUR COLLECTION IS YOUR BUSINESS.</H2>
          <P className="mt-3">Confidentiality is not an upgrade at CardScience HQ. It is part of our service.</P>
          <Link to="/confidentiality" className="btn-ghost inline-flex items-center gap-2 rounded-lg px-5 py-3 text-xs mt-6">
            READ OUR CONFIDENTIALITY POLICY <ArrowRight size={14} />
          </Link>
        </Callout>
      </Wrap>

      {/* Final positioning */}
      <Wrap className="mt-16 text-center">
        <p className="eyebrow mb-5">Final Brand Positioning</p>
        <Prose>
          <P>CardScience HQ is not a grading company.</P>
          <P>It is not a “PSA 10 factory.”</P>
          <P>It is not a miracle restoration service.</P>
          <P>
            It is a collectibles laboratory built around inspection, responsible cleaning where appropriate,
            careful preparation, preservation, presentation and discretion.
          </P>
        </Prose>
        <div className="mt-10 space-y-6">
          <Impact tone="holo">THE GRADER MAKES THE CALL.<br />WE MAKE SURE YOUR CARD SHOWS UP READY.</Impact>
          <Impact>FOR COLLECTORS WHO WANT THEIR COLLECTIONS TO BE THE BEST VERSION OF THEMSELVES.</Impact>
          <Impact tone="holo">CLEAN. PREP. PRESERVE.</Impact>
          <Impact>IF WE DON’T BELIEVE WE SHOULD TOUCH IT,<br />WE WON’T TAKE YOUR MONEY.</Impact>
        </div>
        <Link to="/submit" className="btn-holo inline-flex items-center gap-2 rounded-xl px-8 py-4 text-sm mt-12">
          SUBMIT A COLLECTIBLE <ArrowRight size={16} />
        </Link>
      </Wrap>
    </div>
  );
}
